/**
 * NEXUS UI - MetricCounter
 * Animated number display with scanning effect
 */

import React, { useEffect, useState, useRef, useMemo } from 'react';
import type { BaseProps, NexusVariant, NexusSize, MetricDelta } from './types';

export interface MetricCounterProps extends BaseProps {
  value: number;
  previousValue?: number;
  unit?: string;
  prefix?: string;
  suffix?: string;
  decimals?: number;
  variant?: NexusVariant;
  size?: NexusSize;
  animated?: boolean;
  glow?: boolean;
  delta?: MetricDelta;
  formatFn?: (value: number) => string;
}

const variantColors: Record<NexusVariant, string> = {
  cyan: 'rgba(0, 212, 255, 0.6)',
  blue: 'rgba(59, 130, 246, 0.6)',
  violet: 'rgba(168, 85, 247, 0.6)',
  green: 'rgba(0, 255, 136, 0.6)',
  amber: 'rgba(255, 170, 0, 0.6)',
  red: 'rgba(255, 51, 102, 0.6)',
};

const sizeClasses: Record<NexusSize, string> = {
  sm: '',
  md: '',
  lg: 'nx-metric__value--lg',
  xl: 'nx-metric__value--xl',
};

export const MetricCounter: React.FC<MetricCounterProps> = ({
  value,
  unit,
  prefix,
  suffix,
  decimals = 0,
  variant = 'cyan',
  size = 'md',
  animated = true,
  glow = true,
  delta,
  formatFn,
  className = '',
  style,
}) => {
  const [displayValue, setDisplayValue] = useState(value);
  const [isChanging, setIsChanging] = useState(false);
  const animationRef = useRef<number | null>(null);
  const prevValueRef = useRef(value);

  // Animate value changes
  useEffect(() => {
    if (!animated) {
      setDisplayValue(value);
      return;
    }

    const startValue = prevValueRef.current;
    const endValue = value;
    const duration = 800;
    const startTime = performance.now();

    setIsChanging(true);

    const animate = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Easing function (ease-out-expo)
      const easeOutExpo = 1 - Math.pow(2, -10 * progress);
      const current = startValue + (endValue - startValue) * easeOutExpo;

      setDisplayValue(current);

      if (progress < 1) {
        animationRef.current = requestAnimationFrame(animate);
      } else {
        setDisplayValue(endValue);
        setIsChanging(false);
        prevValueRef.current = endValue;
      }
    };

    animationRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [value, animated]);

  const formattedValue = useMemo(() => {
    if (formatFn) return formatFn(displayValue);

    const fixed = displayValue.toFixed(decimals);
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(parseFloat(fixed));
  }, [displayValue, decimals, formatFn]);

  const digits = formattedValue.split('');

  const renderDelta = () => {
    if (!delta) return null;

    const deltaSign = delta.type === 'positive' ? '+' : delta.type === 'negative' ? '-' : '';
    const deltaClass = `nx-metric__delta nx-metric__delta--${delta.type}`;

    return (
      <span className={deltaClass}>
        <span className="nx-metric__delta-arrow">
          {delta.type === 'positive' && '▲'}
          {delta.type === 'negative' && '▼'}
        </span>
        {deltaSign}
        {Math.abs(delta.value).toFixed(decimals)}
        {unit && <span className="nx-metric__delta-unit">{unit}</span>}
      </span>
    );
  };

  return (
    <div className={`nx-metric ${className}`} style={style}>
      <div
        className={`nx-metric__value ${sizeClasses[size]} ${glow ? 'nx-metric__value--glow' : ''}`}
        style={{
          '--metric-glow': variantColors[variant],
        } as React.CSSProperties}
      >
        {prefix && <span className="nx-metric__prefix">{prefix}</span>}
        {digits.map((digit, index) => (
          <span
            key={`${index}-${digit}`}
            className={`nx-metric__digit ${isChanging ? 'nx-metric__digit--changing' : ''}`}
            style={{
              animationDelay: `${index * 20}ms`,
            }}
          >
            {digit}
          </span>
        ))}
        {suffix && <span className="nx-metric__suffix">{suffix}</span>}
        {unit && <span className="nx-metric__unit">{unit}</span>}
      </div>
      {renderDelta()}
    </div>
  );
};

export default MetricCounter;
